import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaMusic, FaVideo, FaPlay, FaPause, FaCrown, FaRobot, FaShare, FaCheck, FaClock, FaHeadphones, FaEye, FaChevronLeft } from 'react-icons/fa';
import API_CONFIG from '../config/api';
import { decodeId } from '../utils/idMasker';
import './ContentDetailsPage.css';

const ContentDetailsPage = () => {
    const { trackId, videoId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [streamUrl, setStreamUrl] = useState(null);
    const [copied, setCopied] = useState(false);
    const [imageError, setImageError] = useState(false);
    const audioRef = useRef(null);
    const videoRef = useRef(null);

    const contentType = trackId ? 'track' : 'video';
    // Decode the hashed ID from URL
    const encodedId = trackId || videoId;
    const contentId = decodeId(encodedId);

    const fetchContent = useCallback(async () => {
        setLoading(true);
        setError(null);
        setImageError(false);

        try {
            const endpoint = contentType === 'track'
                ? `${API_CONFIG.BASE_URL}/api/v1/tracks/${contentId}`
                : `${API_CONFIG.BASE_URL}/api/v1/videos/${contentId}`;

            const response = await fetch(endpoint);

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error('Content not found');
                }
                throw new Error('Failed to load content');
            }

            const data = await response.json();
            setContent(data);
        } catch (err) {
            console.error('Error fetching content:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [contentId, contentType]);

    useEffect(() => {
        fetchContent();
    }, [fetchContent]);

    const handlePlay = async () => {
        if (isPlaying) {
            if (contentType === 'track' && audioRef.current) {
                audioRef.current.pause();
            } else if (contentType === 'video' && videoRef.current) {
                videoRef.current.pause();
            }
            setIsPlaying(false);
            return;
        }

        if (!streamUrl) {
            try {
                const endpoint = contentType === 'track'
                    ? `${API_CONFIG.BASE_URL}/api/v1/tracks/${contentId}/stream`
                    : `${API_CONFIG.BASE_URL}/api/v1/videos/${contentId}/stream`;

                const response = await fetch(endpoint);

                if (!response.ok) {
                    if (response.status === 402) {
                        alert('This content requires payment to access.');
                        return;
                    }
                    throw new Error('Failed to get stream URL');
                }

                const data = await response.json();
                setStreamUrl(data.stream_url);

                setTimeout(() => {
                    if (contentType === 'track' && audioRef.current) {
                        audioRef.current.play();
                    } else if (contentType === 'video' && videoRef.current) {
                        videoRef.current.play();
                    }
                    setIsPlaying(true);
                }, 100);
            } catch (err) {
                console.error('Error getting stream URL:', err);
                alert('Failed to play content. Please try again.');
            }
        } else {
            if (contentType === 'track' && audioRef.current) {
                audioRef.current.play();
            } else if (contentType === 'video' && videoRef.current) {
                videoRef.current.play();
            }
            setIsPlaying(true);
        }
    };

    const handleShare = async () => {
        const shareUrl = window.location.href;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const getImageUrl = (coverImageUrl) => {
        if (!coverImageUrl) return null;
        if (coverImageUrl.startsWith('http://') || coverImageUrl.startsWith('https://')) {
            return coverImageUrl;
        }
        return `${API_CONFIG.BASE_URL}${coverImageUrl}`;
    };

    const formatDuration = (seconds) => {
        if (!seconds) return null;
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Handle back navigation
    // In React Router v6, location.key === "default" means no previous history
    const handleGoBack = () => {
        if (location.key !== "default") {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    if (loading) {
        return (
            <div className="content-details-page">
                <div className="content-details-container">
                    <div className="content-loading">
                        <div className="loading-spinner"></div>
                        <p>Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !content) {
        return (
            <div className="content-details-page">
                <div className="content-details-container">
                    <div className="content-error">
                        <h2>{error ? 'Error' : 'Not Found'}</h2>
                        <p>{error || 'This content doesn\'t exist.'}</p>
                        <a href="/" className="back-home-btn">Go Home</a>
                    </div>
                </div>
            </div>
        );
    }

    const title = content.title || 'Untitled';
    const artist = content.artist || content.composer_name || content.creator_username || '';
    const coverImage = getImageUrl(content.cover_image_url);
    const isPaid = content.is_paid;
    const price = content.price;
    const duration = content.duration;
    const description = content.description;
    const formattedDuration = formatDuration(duration);
    const playCount = content.play_count || 0;
    const viewCount = content.view_count || 0;

    return (
        <div className="content-details-page">
            <Helmet>
                <title>{title}{artist ? ` - ${artist}` : ''}</title>
                <meta name="description" content={description || `Listen to ${title}`} />
            </Helmet>

            {/* Breadcrumb Navigation */}
            <div className="breadcrumb-bar">
                <button onClick={handleGoBack} className="breadcrumb-back-btn">
                    <FaChevronLeft /> Back to Previous Page
                </button>
            </div>

            <div className="content-details-container">
                <div className="content-details-wrapper">
                    {/* Cover */}
                    <div className="content-media-section">
                        <div className={`content-cover ${contentType}`}>
                            {coverImage && !imageError ? (
                                <img
                                    src={coverImage}
                                    alt={title}
                                    onError={() => setImageError(true)}
                                />
                            ) : (
                                <div className="cover-placeholder">
                                    {contentType === 'track' ? <FaMusic /> : <FaVideo />}
                                </div>
                            )}

                            {contentType === 'track' && (
                                <button
                                    className={`play-overlay-btn ${isPlaying ? 'playing' : ''}`}
                                    onClick={handlePlay}
                                >
                                    {isPlaying ? <FaPause /> : <FaPlay />}
                                </button>
                            )}
                        </div>

                        {contentType === 'track' && streamUrl && (
                            <audio
                                ref={audioRef}
                                src={streamUrl}
                                onEnded={() => setIsPlaying(false)}
                                onPause={() => setIsPlaying(false)}
                                onPlay={() => setIsPlaying(true)}
                                controls
                                className="audio-player"
                            />
                        )}

                        {contentType === 'video' && (
                            <div className="video-player-wrapper">
                                {streamUrl ? (
                                    <video
                                        ref={videoRef}
                                        src={streamUrl}
                                        controls
                                        className="video-player"
                                        onEnded={() => setIsPlaying(false)}
                                        onPause={() => setIsPlaying(false)}
                                        onPlay={() => setIsPlaying(true)}
                                        poster={coverImage}
                                    />
                                ) : (
                                    <button className="play-video-btn" onClick={handlePlay}>
                                        <FaPlay /> Play Video
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <div className="content-info-section">
                        {isPaid && (
                            <div className="premium-badge">
                                <FaCrown className="badge-icon" />
                                Premium{price ? ` · ${price} credits` : ''}
                            </div>
                        )}

                        {isPlaying && contentType === 'track' && (
                            <div className="equalizer">
                                <div className="equalizer-bar"></div>
                                <div className="equalizer-bar"></div>
                                <div className="equalizer-bar"></div>
                                <div className="equalizer-bar"></div>
                            </div>
                        )}

                        <h1 className="content-title">{title}</h1>
                        {artist && <p className="content-artist">{artist}</p>}

                        {/* Meta */}
                        <div className="content-meta">
                            {formattedDuration && (
                                <span className="meta-item">
                                    <FaClock /> {formattedDuration}
                                </span>
                            )}
                            {content.ai_score > 0 && (
                                <span className="meta-item ai-score">
                                    <FaRobot /> AI: {content.ai_score}/5
                                </span>
                            )}
                        </div>

                        {description && (
                            <div className="content-description">
                                <h3>About</h3>
                                <p>{description}</p>
                            </div>
                        )}

                        {(playCount > 0 || viewCount > 0) && (
                            <div className="content-stats">
                                {playCount > 0 && (
                                    <div className="stat-item">
                                        <FaHeadphones />
                                        <span>{playCount.toLocaleString()} plays</span>
                                    </div>
                                )}
                                {viewCount > 0 && (
                                    <div className="stat-item">
                                        <FaEye />
                                        <span>{viewCount.toLocaleString()} views</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="content-actions">
                            <button
                                className={`share-btn ${copied ? 'copied' : ''}`}
                                onClick={handleShare}
                            >
                                {copied ? <FaCheck /> : <FaShare />}
                                {copied ? 'Copied!' : 'Share'}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="powered-by">
                    Shared via <a href="/">YourPlatform</a>
                </div>
            </div>
        </div>
    );
};

export default ContentDetailsPage;
